import React, { useRef, useEffect, useState, useCallback } from 'react';

/**
 * Simple canvas signature pad (mouse + touch).
 */
const SignaturePad = ({ onChange, disabled = false, className = '' }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [isEmpty, setIsEmpty] = useState(true);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    const width = parent?.clientWidth || 400;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = width * ratio;
    canvas.height = 160 * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = '160px';
    const ctx = canvas.getContext('2d');
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, 160);
    setIsEmpty(true);
    onChange?.(null);
  }, [onChange]);

  useEffect(() => {
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setIsEmpty(false);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const data = canvasRef.current.toDataURL('image/png');
    setIsEmpty(false);
    onChange?.(data);
  };

  const clear = () => {
    resize();
  };

  return (
    <div className={className}>
      <div className="rounded-lg border border-gray-300 bg-white keep-light overflow-hidden touch-none">
        <canvas
          ref={canvasRef}
          className={`w-full block ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-crosshair'}`}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <p className="text-xs text-gray-500">Draw your signature above</p>
        <button
          type="button"
          onClick={clear}
          disabled={disabled || isEmpty}
          className="text-xs font-medium text-gray-600 hover:text-gray-900 disabled:opacity-40"
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
